import React, { useEffect, useState } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'src/redux/hooks';
import {
  useListWebinarsForUserQuery,
  useRegisterUserForWebinarMutation,
} from 'src/redux/slices/api/su';
import {
  setWebinars,
  updateWebinarRegistrationStatus,
  selectSortedWebinars,
  selectWebinarById,
} from 'src/redux/slices/webinar';
import { WebinarCarousel } from 'src/components/carousel';
import LoadingView from 'src/components/LoadingView';
import ErrorView from 'src/components/ErrorView';
import { getStatusCodeFromError } from 'src/utils/error';
import useMetrics from 'src/metrics/useUserMetrics';
import usePagePerformance from 'src/metrics/usePagePerformance';
import { FeatureName } from 'src/metrics/metricsFields';
import { useBundle } from '@amzn/react-arb-tools';
import { t } from 'src/utils/translation';
import { BUNDLE_NAME, WEBINAR_STRING_ID } from 'src/constants/localization';
import { WebinarData } from 'src/types/state/webinar';
import { buildWebinarRegistrationRequest } from 'src/utils/webinarRegistration';
import { WebinarRegistrationModal } from '@amzn/seller-university-ui-components';
import { parseHtmlToReact, hasDescriptionContent } from 'src/utils/htmlParser';
import { mergeUrlParameters } from 'src/utils/routing';
import { extractRefTag } from 'src/utils/refParameterConversion';
import { REF_TAG } from 'src/constants/refTag';
import {
  WEBINAR_REGISTRATION_STATUS, WEBINAR_MODAL_STATE, WebinarModalState, FALLBACK_TIMEZONE,
} from 'src/constants/webinar';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import { getWebinarRegistrationStatus } from 'src/utils/webinarDataTransformer';

/** WebinarPage Component */
const WebinarPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { viewMetric, clickMetric } = useMetrics();
  const [webinarBundle] = useBundle(BUNDLE_NAME.WEBINAR);

  // Modal state management
  const [modalState, setModalState] = useState<WebinarModalState>(WEBINAR_MODAL_STATE.REGISTRATION);
  const [isRegistering, setIsRegistering] = useState(false);
  const [pendingRegistrationData, setPendingRegistrationData] = useState<{
    registrantURL: string;
  } | null>(null);

  // Get webinar ID from URL params
  const selectedWebinarId = searchParams.get('webinarId');

  // Capture the inbound referral tag once on entry. handleWebinarClick later overwrites
  // ref_ with the modal tag, so reading it at registration time would lose the surface
  // that referred the seller.
  const [inboundRefTag] = useState(() => extractRefTag(searchParams));

  // Redux state
  const webinars = useAppSelector(selectSortedWebinars);
  const selectedWebinar = useAppSelector((state) => (
    selectedWebinarId ? selectWebinarById(state, selectedWebinarId) : null
  ));

  // API queries and mutations
  const {
    data: webinarsData,
    isLoading: isWebinarsLoading,
    isError: isWebinarsError,
    error: webinarsErrorObj,
  } = useListWebinarsForUserQuery();

  const [registerUserForWebinar] = useRegisterUserForWebinarMutation();

  // Track per-page performance (latency + availability) once the webinar list finishes loading.
  usePagePerformance(FeatureName.WebinarPage, isWebinarsLoading, isWebinarsError);

  // Track page view on mount
  useEffect(() => {
    viewMetric({
      feature: FeatureName.WebinarPage,
      metadata: {
        path: window.location.pathname,
        selectedWebinarId: selectedWebinarId || undefined,
      },
    });
  }, [selectedWebinarId, viewMetric]);

  // Update Redux store when webinars data is loaded
  useEffect(() => {
    if (webinarsData?.webinars) {
      dispatch(setWebinars(webinarsData.webinars));
    }
  }, [webinarsData, dispatch]);

  // Handle webinar registration
  const handleWebinarRegistration = async () => {
    if (!selectedWebinarId || !selectedWebinar) return;

    // Track modal register button click
    clickMetric({
      feature: FeatureName.WebinarButtonClick,
      metadata: {
        webinarId: selectedWebinar.webinarId,
        webinarTitle: selectedWebinar.title,
        registrationStatus: selectedWebinar.registrationStatus,
        buttonLocation: 'modal',
        buttonAction: 'register',
        modalState,
      },
    });

    setIsRegistering(true);

    try {
      // Build the complete registration request using the utility function
      const registrationRequest = buildWebinarRegistrationRequest(
        selectedWebinar,
        undefined,
        undefined,
        inboundRefTag,
      );

      const result = await registerUserForWebinar(registrationRequest).unwrap();

      // Store registration data temporarily - don't update webinar state yet
      setPendingRegistrationData({
        registrantURL: result.webinarRegistrant.registrationUrl,
      });

      // Show success state
      setModalState(WEBINAR_MODAL_STATE.SUCCESS);
    } catch (error) {
      // If registration fails, set the webinar back to unregistered state
      dispatch(updateWebinarRegistrationStatus({
        webinarId: selectedWebinarId,
        registrationStatus: WEBINAR_REGISTRATION_STATUS.REGISTER,
        registrantURL: '',
      }));

      // Show error state
      setModalState(WEBINAR_MODAL_STATE.ERROR);
    } finally {
      setIsRegistering(false);
    }
  };

  // Handle webinar card click (open modal)
  const handleWebinarClick = (webinarId: string) => {
    const webinar = webinars.find((w: WebinarData) => w.webinarId === webinarId);

    if (webinar) {
      // Preserve existing parameters and add webinarId and refTag
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('webinarId', webinarId);
        newParams.set('ref_', REF_TAG.WEBINAR_CARD_REGISTER);
        return newParams;
      });

      // Set appropriate modal state based on registration status
      if (webinar.registrationStatus === WEBINAR_REGISTRATION_STATUS.REGISTERED
          || webinar.registrationStatus === WEBINAR_REGISTRATION_STATUS.JOIN) {
        setModalState(WEBINAR_MODAL_STATE.SUCCESS);
      } else {
        // Default case - assumes REGISTER status or any other status
        setModalState(WEBINAR_MODAL_STATE.REGISTRATION);
      }
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    // If we're closing a success modal, update the webinar state with correct registration status
    if (modalState === WEBINAR_MODAL_STATE.SUCCESS && pendingRegistrationData && selectedWebinarId && selectedWebinar) {
      // Recalculate registration status based on current time to handle 15-minute join window
      const correctStatus = getWebinarRegistrationStatus(
        true, // isRegistered = true (user just registered)
        selectedWebinar.webinarScheduledTimestamp,
        selectedWebinar.duration,
        selectedWebinar.userTimezone || FALLBACK_TIMEZONE,
      );

      dispatch(updateWebinarRegistrationStatus({
        webinarId: selectedWebinarId,
        registrationStatus: correctStatus,
        registrantURL: pendingRegistrationData.registrantURL,
      }));
      setPendingRegistrationData(null);
    }

    // Remove webinarId and ref_ parameters, preserve other parameters
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('webinarId');
      newParams.delete('ref_');
      return newParams;
    });
    // Reset modal state when closing
    setModalState(WEBINAR_MODAL_STATE.REGISTRATION);
  };

  // URL validation and modal state management for registered webinars
  useEffect(() => {
    if (selectedWebinarId && selectedWebinar) {
      // If webinar is already registered, show success modal
      if (selectedWebinar.registrationStatus === WEBINAR_REGISTRATION_STATUS.REGISTERED) {
        setModalState(WEBINAR_MODAL_STATE.SUCCESS);
      } else if (selectedWebinar.registrationStatus === WEBINAR_REGISTRATION_STATUS.JOIN) {
        // If webinar is in JOIN state, show success modal
        setModalState(WEBINAR_MODAL_STATE.SUCCESS);
      } else if (selectedWebinar.registrationStatus === WEBINAR_REGISTRATION_STATUS.REGISTER) {
        // If webinar needs registration, show registration modal
        setModalState(WEBINAR_MODAL_STATE.REGISTRATION);
      }
    } else if (selectedWebinarId && !selectedWebinar && webinars.length > 0) {
      // If webinar ID in URL exists but webinar not found, remove only webinarId parameter
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('webinarId');
        return newParams;
      });
    }
  }, [selectedWebinarId, selectedWebinar, webinars.length, setSearchParams]);

  // Check if selected webinar exists and modal should be visible
  // Modal should be visible for any valid webinar with ID in URL
  const isModalVisible = Boolean(
    selectedWebinarId
    && selectedWebinar
    && (selectedWebinar.registrationStatus === WEBINAR_REGISTRATION_STATUS.REGISTER
        || selectedWebinar.registrationStatus === WEBINAR_REGISTRATION_STATUS.REGISTERED
        || selectedWebinar.registrationStatus === WEBINAR_REGISTRATION_STATUS.JOIN
        || modalState === WEBINAR_MODAL_STATE.SUCCESS
        || modalState === WEBINAR_MODAL_STATE.ERROR),
  );

  // Get modal configuration based on current state
  const getModalConfig = () => {
    // Test hook to force null
    if (process.env.NODE_ENV === 'test' && (window as any).testForceModalConfigNull) {
      return null;
    }
    if (!selectedWebinar) return null;

    const baseTimings = `${selectedWebinar.date} | ${selectedWebinar.startTime} - ${selectedWebinar.endTime} ${
      selectedWebinar.userTimezone
    }`;

    if (modalState === WEBINAR_MODAL_STATE.SUCCESS) {
      // For JOIN state webinars, show Join button as primary action
      const isJoinState = selectedWebinar.registrationStatus === WEBINAR_REGISTRATION_STATUS.JOIN;

      return {
        title: t(webinarBundle, WEBINAR_STRING_ID.MODAL_SUCCESS_TITLE_TEXT),
        bodyTitle: selectedWebinar.title,
        timings: baseTimings,
        contentHeader: undefined,
        content: t(webinarBundle, WEBINAR_STRING_ID.MODAL_SUCCESS_MESSAGE_TEXT),
        secondaryButton: {
          text: t(webinarBundle, WEBINAR_STRING_ID.CLOSE_BUTTON_TEXT),
          onClick: () => handleModalClose(),
        },
        primaryButton: isJoinState && selectedWebinar.registrantURL ? {
          text: t(webinarBundle, WEBINAR_STRING_ID.JOIN_BUTTON_TEXT),
          onClick: () => {
            // Track modal join button click
            clickMetric({
              feature: FeatureName.WebinarButtonClick,
              metadata: {
                webinarId: selectedWebinar.webinarId,
                webinarTitle: selectedWebinar.title,
                registrationStatus: selectedWebinar.registrationStatus,
                buttonLocation: 'modal',
                buttonAction: 'join',
                modalState,
              },
            });

            // Open registration URL in new tab with ref tag
            const urlWithRefTag = mergeUrlParameters(
              selectedWebinar.registrantURL,
              `?ref_=${REF_TAG.WEBINAR_MODAL_JOIN}`,
            );
            window.open(urlWithRefTag, '_blank');
          },
        } : null,
      };
    }

    if (modalState === WEBINAR_MODAL_STATE.ERROR) {
      return {
        title: t(webinarBundle, WEBINAR_STRING_ID.MODAL_ERROR_TITLE_TEXT),
        bodyTitle: selectedWebinar.title,
        timings: baseTimings,
        contentHeader: undefined,
        content: t(webinarBundle, WEBINAR_STRING_ID.MODAL_ERROR_MESSAGE_TEXT),
        secondaryButton: {
          text: t(webinarBundle, WEBINAR_STRING_ID.CLOSE_BUTTON_TEXT),
          onClick: () => handleModalClose(),
        },
        primaryButton: null,
      };
    }

    // Default case: REGISTRATION modal
    return {
      title: t(webinarBundle, WEBINAR_STRING_ID.MODAL_TITLE_TEXT),
      bodyTitle: selectedWebinar.title,
      timings: baseTimings,
      contentHeader: hasDescriptionContent(selectedWebinar.description)
        ? t(webinarBundle, WEBINAR_STRING_ID.SESSION_OVERVIEW_TEXT)
        : undefined,
      content: parseHtmlToReact(selectedWebinar.description),
      secondaryButton: {
        text: t(webinarBundle, WEBINAR_STRING_ID.CANCEL_BUTTON_TEXT),
        onClick: () => handleModalClose(),
        disabled: isRegistering,
      },
      primaryButton: {
        text: t(webinarBundle, WEBINAR_STRING_ID.REGISTER_BUTTON_TEXT),
        onClick: () => handleWebinarRegistration(),
        disabled: isRegistering,
      },
    };
  };

  // Test hooks for coverage (only in test environment)
  if (process.env.NODE_ENV === 'test') {
    (window as any).testHandleWebinarRegistration = handleWebinarRegistration;
    (window as any).testHandleWebinarClick = handleWebinarClick;
    (window as any).testGetModalConfig = getModalConfig;
  }

  // Render loading state
  if (isWebinarsLoading) {
    return <LoadingView />;
  }

  // Render error state
  if (isWebinarsError) {
    ClientLogger.error(ClientLog.IllegalInternalDataState, {
      path: location.pathname,
      url: window.location.href,
    });
    return <ErrorView statusCode={getStatusCodeFromError(webinarsErrorObj)} />;
  }

  return (
    <div className="px-su-page-padding" data-testid="webinar-page" style={{ position: 'relative' }}>
      {/* Page Header */}
      <section className="mb-[36px]" data-testid="webinars-section">
        {/* Webinar Carousel */}
        {webinars.length > 0 ? (
          <>
            <div className="mx-auto max-w-7xl px-su-space-08">
              <h1
                className="mx-[28px] !mb-su-space-05 !pt-[36px] !text-su-heading-500
                  !font-bold !text-su-text-primary"
              >
                {t(webinarBundle, WEBINAR_STRING_ID.UPCOMING_WEBINARS_TITLE)}
              </h1>
            </div>
            <WebinarCarousel
              webinars={webinars}
              onWebinarClick={handleWebinarClick}
            />
          </>
        ) : (
          <div className="mx-auto max-w-7xl px-su-space-08">
            <h1
              className="mx-[28px] !mb-su-space-05 !pt-[36px] !text-su-heading-500
                !font-bold !text-su-text-primary"
            >
              {t(webinarBundle, WEBINAR_STRING_ID.NO_UPCOMING_WEBINAR_TITLE)}
            </h1>
          </div>
        )}
      </section>

      {/* Registration Modal */}
      {isModalVisible && selectedWebinar && (() => {
        const modalConfig = getModalConfig();
        return modalConfig ? (
          <WebinarRegistrationModal
            isVisible={isModalVisible}
            onClose={handleModalClose}
            modalId={selectedWebinar.webinarId}
            {...modalConfig}
          />
        ) : null;
      })()}
    </div>
  );
};

export default WebinarPage;
